import { useState } from "react";
import {
  Paper,
  Text,
  Title,
  Avatar,
  Badge,
  useMantineTheme,
  rem,
  Button,
} from "@mantine/core";
import classes from "./CustomerStories.module.css";
import { useTranslation } from 'react-i18next';

interface CardProps {
  stars: number;
  comments: string;
  customerName: string;
  customerPosition: string;
}

function Card({ stars, comments, customerName, customerPosition }: CardProps) {
  const renderStars = (count: number) => {
    const starElements = [];
    for (let i = 0; i < count; i++) {
      starElements.push(
        <Badge variant="transparent" color="orange" size="xl" key={i}>
          ★
        </Badge>
      );
    }
    return starElements;
  };

  return (
    <div className={classes.page}>
      <Paper shadow="md" p="xl" radius="lg" className={classes.card}>
        <div className={classes.stars}>{renderStars(stars)}</div>
        <Text className={classes.comments}>{comments}</Text>
        <div className={classes.avatar}>
          <Avatar
            size="sm"
            radius="lg"
            src="/avatar.png"
            alt="Customer Avatar"
          />
          <div>
            <Title order={4} className={classes.name}>
              {customerName}
            </Title>
            <Text className={classes.position}>{customerPosition}</Text>
          </div>
        </div>
      </Paper>
    </div>
  );
}

const cs = [
  {
    stars: 5,
    comments:
      "Really happy with Domax's service! They were responsive, easy to deal with, and delivered exactly what we needed without any hassle. Will definitely work with them again. Highly recommended!",
    customerName: "Encik Faizal",
    customerPosition: "Director, IT Company",
  },
  {
    stars: 5,
    comments:
      "Domax has been very consistent with their delivery and quality. Communication is smooth and the team is always helpful.",
    customerName: "Encik Zulkifli",
    customerPosition: "Operations Director, Manufacturing Company",
  },
  {
    stars: 5,
    comments:
      "Very professional and reliable. They understand our urgency and always come through for us. Pricing is fair and the service is top notch. Happy to recommend Domax!",
    customerName: "Mr. Tan",
    customerPosition: "Managing Director, Trading Company",
  },
  {
    stars: 5,
    comments:
      "Great experience working with Domax. They listen, they deliver, and they don't overpromise. Exactly the kind of partner we were looking for.",
    customerName: "Encik Hairul",
    customerPosition: "CEO, Consulting Firm",
  },
  {
    stars: 5,
    comments:
      "Smooth collaboration from start to finish. The team was professional and easy to communicate with. Everything was handled properly with no issues. Will engage them again for future projects",
    customerName: "Mr. Lim",
    customerPosition: "Director, Logistic Company",
  },
  {
    stars: 5,
    comments:
      "Domax Sdn Bhd has been a reliable partner for our engineering projects over the past two years. Their team demonstrates strong technical knowledge and a high level of commitment to quality. Project timelines were consistently met, and any issues raised were resolved promptly. I have no hesitation in recommending Domax to any organisation looking for a trustworthy and capable business partner.",
    customerName: "Mr. Leong",
    customerPosition: "General Manager, Engineering Company",
  },
];

export function CustomerStories() {
    const { t } = useTranslation();
  const theme = useMantineTheme();
  const cardsPerPage = 2;
  const totalCards = cs.length;
  const totalPages = Math.ceil(totalCards / cardsPerPage);

  const [currentPage, setCurrentPage] = useState(1);

  const goToNextPage = () => {
    setCurrentPage((prevPage) =>
      prevPage + 1 > totalPages ? 1 : prevPage + 1
    );
  };

  const goToPrevPage = () => {
    setCurrentPage((prevPage) =>
      prevPage - 1 < 1 ? totalPages : prevPage - 1
    );
  };

  const startCardIndex = (currentPage - 1) * cardsPerPage;
  const endCardIndex = Math.min(currentPage * cardsPerPage, totalCards);
  const displayedCards = cs
    .slice(startCardIndex, endCardIndex)
    .map((item, index) => (
      <div key={index} className={classes.item}>
        <Card
          stars={item.stars}
          comments={item.comments}
          customerName={item.customerName}
          customerPosition={item.customerPosition}
        />
      </div>
    ));

  const indicators = Array.from({ length: totalPages }, (_, index) => (
    <div
      key={index}
      className={classes.pageIndicators}
      style={{
        width: rem(8),
        height: rem(8),
        borderRadius: "50%",
        backgroundColor:
          currentPage === index + 1
            ? theme.colors.orange[6]
            : theme.colors.gray[4],
        margin: `0 ${rem(4)}`,
      }}
    />
  ));

  return (
    <div className={classes.page}>
      <div className={classes.title}>
        <Title order={2}>{t('home.section5Title')}</Title>
      </div>
      <div>
        <Text className={classes.text}>
          {t('home.section5Description')}
        </Text>
      </div>
      <div className={classes.displayedCards}>{displayedCards}</div>
      <div className={classes.footer}>
        <div className={classes.indicators}>{indicators}</div>
        <div className={classes.arrows}>
          <Button variant="transparent" onClick={goToPrevPage}>
            {/* Placeholder for left arrow */}
            &lt;
          </Button>
          <Button variant="transparent" onClick={goToNextPage}>
            {/* Placeholder for right arrow */}
            &gt;
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CustomerStories;
